/* Imports needed for all question form subclasses. */
import * as React from 'react';
import { withRouter } from 'react-router';
import { compose } from 'lodash/fp';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import * as uuid from 'uuid';
import ReactTooltip from 'react-tooltip';
import {
    mapStateToQuestionFormBaseProps,
    mapDispatchToQuestionFormBaseProps
} from '../../QuestionFormBase';

import {
    QuestionFormWithOptionsBase,
    QuestionFormWithOptionsBaseProps,
    QuestionFormWithOptionsBaseStates
} from '../QuestionFormWithOptionsBase';
import {
    IPopupCard,
    MULTIMEDIA_TYPES,
    OptionData,
    PROJECT_STATUS,
    ProbeSettings,
    QTYPES,
    Question,
    TradeoffTextCard
} from '@nexxt/common/types';
import NotifyHOC from '../../../../../../HOC/Notifications';

/* Components used in this question form. */
import BuilderHeader from '../../../common/builderHeader';
import QuestionSettings from '../../../common/questionSettings';
import OptionPackModal from '../../../../../libraries/optionpack/OptionPackModal';
import { OPTION_TYPES, TRADE_OFF_TYPES } from '@nexxt/common/types';
import Toggle from '../../../../../common/Toggle';
import MediaLibraryAddModal from '../../../../../libraries/mediaLibrary/MediaLibraryAddModal';
import ImageModal from '../../../common/ImageModal';
import InputRadio from '../../../common/radioButton/inputRadio';
import {
    DEFAULT_TEXT,
    FOLLOW_UP_TEXTS
} from '../../../../../../constants/defaultText';
import CollaspeButton from '../../../common/optionCard/CollaspeButton';
import { alertMessage } from '../../../../../../constants/alertMessage';
import {
    STATIC_STORAGE_URL,
    TRADE_OFF_TEXT_BASED_OPTION_CARD
} from '@nexxt/common/constants';
import Options from '../../../common/Options';
import MultimediaBubble from '../../../common/MultimediaBubble';
import {
    BUTTON_WITH_TRANSLATION,
    DEFAULT_BUTTON,
    DEFAULT_TRANSLATION_BUTTONS
} from '../../../../../../constants/defaultOption';
import TradeOffText from '../../../common/tradeoff/TradeOffText';
import { resetKey } from '../../../../../../utils/question';
import { createNewRichTextObject } from '@nexxt/common/services/TextService';

interface Props extends QuestionFormWithOptionsBaseProps {
    // nothing special... yet!
    t?: (text: string, options?: Record<string, boolean>) => string; // translate function
}

interface States extends QuestionFormWithOptionsBaseStates {
    // nothing special... yet!
    followUpType: any[];
    currentImage: string;
    buttonType: number;
    currentTab: number;
}

const EMPTY_TRADEOFF_CARD: OptionData = {
    label: '',
    text: [],
    value: '1'
};

class TradeOff extends QuestionFormWithOptionsBase<Props, States> {
    constructor(p: QuestionFormWithOptionsBaseProps) {
        super(p, QTYPES.TRADE_OFF);
        // const isTypeImage = this.state.question.options.every(
        //     (option) => option?.mediaURL
        // );
        const isTypeImage = !p.currentQuestion.isRichText;
        const question: Question = {
            ...this.state.question,
            title: this.getInitialQuestionField(
                DEFAULT_TEXT.QUESTION.TRADE_OFF.TITLE,
                this.state.question.title
            ),
            showAlternateWording: true,
            showFollowUp: this.getInitialQuestionField(
                true,
                this.state.question.showFollowUp
            ),
            min: 5,
            max: 30,
            buttons: this.getInitialQuestionField(
                DEFAULT_TRANSLATION_BUTTONS(
                    QTYPES.TRADE_OFF,
                    this.props.t,
                    this.props.currentProject.languages.map((l) => l.value)
                )
                    .slice(1, 4)
                    .map((b, i) => ({ ...b, value: `${i + 1}` })),
                this.state.question.buttons
            ),
            probeSettings: this.getInitialProbeSettings(this.state.question),
            random: this.props.isNewQuestion
                ? true
                : this.state.question.random,
            tradeoffSettings: {
                // TODO: Set TEXT VS IMAGE depending on current option array...
                tradeoffTypes: isTypeImage
                    ? TRADE_OFF_TYPES.IMAGE
                    : TRADE_OFF_TYPES.TEXT
            },
            isRichText: !isTypeImage,
            // todo - replace isVisibile with new column in entity
            isVisible: this.props.isNewQuestion
                ? false
                : this.state.question.isVisible
        };
        // check if tradeoffCards is populated
        // if it's not populated then initiate with empty card
        if (!question?.options) {
            question.options = [EMPTY_TRADEOFF_CARD];
        }

        this.state = {
            ...this.state,
            showCardSettings: true,
            followUpType: FOLLOW_UP_TEXTS.TRADE_OFF,
            buttonType: this.props.isNewQuestion ? 3 : question.buttons.length,
            currentTab: question?.options?.length
                ? question.options.length - 1
                : 0,
            question
        };
    }

    handleTradeoffTextPiping = () => {
        this.setState(
            {
                question: {
                    ...this.state.question,
                    piping: !this.state.question.piping
                } as Question
            },
            this.setEditFlag
        );
    };

    isImageType = (options) => {
        return options.every(
            (option) => option?.mediaURL != TRADE_OFF_TEXT_BASED_OPTION_CARD
        );
    };

    getInitialProbeSettings = (q: Question): ProbeSettings => {
        const defaultProbeSettingsForFollowUp = {
            lazyAnswer: true,
            gibberish: true,
            showGenericProbe: true
        };
        return {
            followUpTextsLike: this.getInitialQuestionField(
                defaultProbeSettingsForFollowUp,
                q?.probeSettings?.followUpTextsLike || {}
            ),
            followUpTextsDislike: this.getInitialQuestionField(
                defaultProbeSettingsForFollowUp,
                q?.probeSettings?.followUpTextsDislike || {}
            )
        };
    };

    onButtonChanged = (buttonType) => {
        const currentButtonType = this.state.buttonType;
        if (buttonType === currentButtonType) return;
        let buttons = DEFAULT_TRANSLATION_BUTTONS(
            QTYPES.TRADE_OFF,
            this.props.t,
            this.props.currentProject.languages.map((l) => l.value)
        );
        if (buttonType === 5) {
            buttons = [
                buttons[0],
                {
                    ...buttons[1],
                    mediaURL: `${STATIC_STORAGE_URL}/img/emojis/simple/dislike_light.png`
                },
                buttons[2],
                {
                    ...buttons[3],
                    mediaURL: `${STATIC_STORAGE_URL}/img/emojis/simple/like_light.png`
                },
                buttons[4]
            ];
        } else if (buttonType === 2) {
            buttons = [buttons[1], buttons[3]];
        } else {
            buttons = buttons.slice(1, 4);
        }

        buttons = buttons.map((b, i) => ({ ...b, value: `${i + 1}` }));
        this.setState(
            {
                buttonType,
                question: {
                    ...this.state.question,
                    buttons
                }
            },
            this.setEditFlag
        );
    };

    updateTradeoffTextCard = (data) => {
        this.setState(
            {
                question: {
                    ...this.state.question,
                    options: [...data]
                } as Question
            },
            this.setEditFlag
        );
    };

    handleRichText = (
        cIndex,
        keyName,
        value,
        language,
        index,
        type = MULTIMEDIA_TYPES.RICH_TEXT
    ) => {
        try {
            const optionField = [...(this.state.question.options || [])];
            const currentCardsObject =
                this.state.question.options[cIndex][keyName] || [];
            let uploadedValue = false;
            const newTextField = currentCardsObject.filter(
                (c) => c.language === language
            )[0] || {
                language,
                contents: [
                    {
                        id: uuid.v4(),
                        richText: createNewRichTextObject(''),
                        type
                    }
                ]
            };
            newTextField.contents[0].richText = value;
            const currentLanguage = currentCardsObject.map((c) => {
                if (c.language === language) {
                    uploadedValue = true;
                    return { ...c, contents: newTextField.contents };
                }
                return c;
            });
            let newOptionField;
            if (!uploadedValue) {
                newOptionField = optionField.map((field, sindex) => {
                    if (cIndex !== sindex) return field;
                    return {
                        ...field,
                        [keyName]: [...currentLanguage, newTextField],
                        texts: [...currentLanguage, newTextField]
                    };
                });
                this.updateTradeoffTextCard(newOptionField);
                return;
            }

            newOptionField = optionField.map((field, sindex) => {
                if (cIndex !== sindex) return field;
                return {
                    ...field,
                    [keyName]: [...currentLanguage],
                    texts: [...currentLanguage]
                };
            });
            this.updateTradeoffTextCard(newOptionField);
        } catch (error) {
            console.log('error', error);
        }
    };

    /**
     * Add new Text Card
     */
    addNewTextCard = () => {
        const options: any[] = [...(this.state.question.options || [])];

        options.push({
            label: '',
            mediaURL: TRADE_OFF_TEXT_BASED_OPTION_CARD,
            // text: [],
            value: String(options.length + 1)
        });
        this.setState(
            {
                question: {
                    ...this.state.question,
                    options
                },
                currentTab: options.length - 1
            },
            this.setEditFlag
        );
    };

    deleteTextCard = (index) => {
        if (index >= 0) {
            const optionFields = [...(this.state.question.options || [])];

            optionFields.splice(index, 1);
            // reset the values...
            optionFields.forEach((optionField, index) => {
                optionField.value = String(index + 1);
            });

            this.setState(
                (prevState) => ({
                    question: {
                        ...prevState.question,
                        options: optionFields
                    },

                    currentTab: 0
                }),
                this.setEditFlag
            );
        }
    };

    setTradeoffTextLabel = (label: string) => {
        const options: any[] = [...(this.state.question.options || [])];

        options[this.state.currentTab].label = label;

        this.setState({
            question: {
                ...this.state.question,
                options
            }
        });
    };

    toggleFollowUpProbing = (
        followUpType: 'followUpTextsLike' | 'followUpTextsDislike',
        value: boolean
    ) => {
        const { question } = this.state;
        this.setState(
            {
                question: {
                    ...question,
                    probeSettings: {
                        ...question.probeSettings,
                        [followUpType]: {
                            lazyAnswer: value,
                            gibberish: value,
                            showGenericProbe: value
                        }
                    }
                }
            },
            this.setEditFlag
        );
    };
    render() {
        const { currentProject } = this.props;
        const {
            question,
            isImageModalOpen,
            isOptionExpand,
            isSettingBoxLayoutOpen,
            isMediaLibraryOpen,
            isOptionPackModalOpen,
            followUpType
        } = this.state;
        const questionError =
            (this.state.showError && this.state.questionError) || {};

        return (
            <div>
                <div className="project-add--question-builder">
                    {isOptionPackModalOpen && (
                        <OptionPackModal
                            isOpen={isOptionPackModalOpen}
                            onCancel={this.toggleOptionPackModal}
                            onDone={this.onSelectOpenPack}
                        />
                    )}
                    {isMediaLibraryOpen && (
                        <MediaLibraryAddModal
                            isOpen={this.state.isMediaLibraryOpen}
                            isMultiple={true}
                            setSelectedMedia={this.selectMedia}
                            notify={this.props.notify}
                            onCancel={this.closeMediaLibrary}
                            onClose={this.closeMediaLibrary}
                            type={this.state.mediaType}
                            showMediaLibrary={true}
                        />
                    )}
                    {isImageModalOpen && (
                        <ImageModal
                            isOpen={isImageModalOpen}
                            onClose={this.toggleImageModal}
                            imageURL={this.state.currentImage}
                        />
                    )}
                    <BuilderHeader
                        toggleSettingBoxLayout={this.toggleSettingBoxLayout}
                        isSettingBoxLayoutOpen={isSettingBoxLayoutOpen}
                        showAlert={!this.props.editEnabled}
                        alertMessages={this.getWarningMessage()}
                        question={question}
                        setValue={this.setQuestion}
                        disabled={!this.props.editEnabled}
                        errorObject={this.state.questionError}
                    />
                    <div className="builder-body">
                        {(isSettingBoxLayoutOpen && (
                            <QuestionSettings
                                onClose={this.toggleSettingBoxLayout}
                                question={question}
                                questionSettings={{
                                    allowUserEdit: true,
                                    tradeoffTypes: true,
                                    allowSkipFirstStage: true
                                }}
                                setValue={this.setQuestion}
                                disabled={!this.props.editEnabled}
                            />
                        )) ||
                            null}
                        {this.renderErrorNotes()}
                        <MultimediaBubble
                            question={question}
                            languages={currentProject.languages}
                            setValue={this.setQuestion}
                            setValueWithLanguage={
                                this.NEW_setQuestionTextTranslation
                            }
                            addNewBubble={this.NEW_addNewEditor}
                            removeBubble={this.NEW_removeEditor}
                            disabled={!this.props.editEnabled}
                            questionError={questionError}
                            defaultText={
                                DEFAULT_TEXT.QUESTION.TRADE_OFF.QUESTION_TEXT
                            }
                            questionText={
                                DEFAULT_TEXT.QUESTION.TRADE_OFF.QUESTION_LABEL
                            }
                            settings={{ showClose: true }}
                        />
                        <MultimediaBubble
                            question={question}
                            languages={currentProject.languages}
                            setValue={this.setQuestion}
                            setValueWithLanguage={
                                this.NEW_setQuestionTextTranslation
                            }
                            keyName="alternateWordingTexts"
                            disableAddBubble
                            disabled={!this.props.editEnabled}
                            questionError={questionError}
                            defaultText={
                                DEFAULT_TEXT.QUESTION.TRADE_OFF
                                    .ALTERNATIVE_QUESTION_TEXT
                            }
                            questionText={
                                DEFAULT_TEXT.QUESTION.TRADE_OFF
                                    .ALTERNATIVE_QUESTION_LABEL
                            }
                            settings={{}}
                        />
                        {this.state.question.tradeoffSettings.tradeoffTypes ===
                            TRADE_OFF_TYPES.TEXT && (
                            <React.Fragment>
                                <div
                                    className="row"
                                    data-tip="custom show"
                                    data-for="pipe-option-helper"
                                    data-delay-show="500"
                                    style={{
                                        paddingBottom: 10,
                                        paddingTop: 10,
                                        marginRight: 1.5,
                                        marginLeft: 'auto',
                                        maxWidth: 'fit-content',
                                        justifyContent: 'flex-end',
                                        alignItems: 'center'
                                    }}
                                >
                                    <Toggle
                                        checked={this.state.question.piping}
                                        // disabled={this.state.allowTextPiping}
                                        onChange={() => {
                                            this.handleTradeoffTextPiping();
                                        }}
                                        label="PIPED OPTIONS"
                                    />
                                    <ReactTooltip
                                        className="tooltip"
                                        id="pipe-option-helper"
                                        aria-haspopup="true"
                                        effect="solid"
                                        place="top"
                                    >
                                        <span>
                                            Piped options allow the user to pipe
                                            answer choices from a previous
                                            question
                                        </span>
                                    </ReactTooltip>
                                </div>

                                {!this.state.question.piping && (
                                    <>
                                        <label className="col-md-12 form-group-header">
                                            Concepts & Concept Settings
                                        </label>
                                        <div className="builder-tap">
                                            <div className="builder-tap--wrap">
                                                {this.state.question.options.map(
                                                    (card, index) => {
                                                        const title: string =
                                                            card.label || '';
                                                        const error =
                                                            (this.state
                                                                .showError &&
                                                                this.state
                                                                    .questionError
                                                                    .popupCards) ||
                                                            {};
                                                        const errorCard =
                                                            error[index] &&
                                                            (error[index]
                                                                .title !== '' ||
                                                                error[index]
                                                                    .mediaURL !==
                                                                    '' ||
                                                                error[index]
                                                                    .questionTexts
                                                                    .isError);

                                                        return (
                                                            <div
                                                                key={index}
                                                                style={{
                                                                    position:
                                                                        'relative'
                                                                }}
                                                            >
                                                                <li
                                                                    className={`builder-tap--item ${
                                                                        this
                                                                            .state
                                                                            .currentTab ===
                                                                        index
                                                                            ? ' active'
                                                                            : ''
                                                                    } `}
                                                                    onClick={(
                                                                        e
                                                                    ) =>
                                                                        this.setState(
                                                                            {
                                                                                currentTab:
                                                                                    index
                                                                            }
                                                                        )
                                                                    }
                                                                >
                                                                    <a>
                                                                        {title
                                                                            ? title.substr(
                                                                                  0,
                                                                                  10
                                                                              )
                                                                            : `Cards (${
                                                                                  index +
                                                                                  1
                                                                              })`}
                                                                        &nbsp;
                                                                    </a>
                                                                </li>
                                                            </div>
                                                        );
                                                    }
                                                )}
                                                {this.props.editEnabled && (
                                                    <>
                                                        <div className="builder-tap--options-wrapper">
                                                            <button
                                                                className="btn btn--md btn--black mb-4"
                                                                disabled={
                                                                    !this.props
                                                                        .editEnabled
                                                                }
                                                                onClick={
                                                                    this
                                                                        .addNewTextCard
                                                                }
                                                            >
                                                                Add Text Concept
                                                                Card
                                                            </button>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                )}

                                {question.options.length > 0 &&
                                    !this.state.question.piping && (
                                        <>
                                            <div className="mb-3 ml-3 w-50">
                                                <label className="form-group__label form-group__label--block">
                                                    Concept Label
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-group__control"
                                                    placeholder=""
                                                    onChange={(
                                                        event: React.ChangeEvent<HTMLInputElement>
                                                    ) =>
                                                        this.setTradeoffTextLabel(
                                                            event.target.value
                                                        )
                                                    }
                                                    value={
                                                        this.state.question
                                                            .options[
                                                            this.state
                                                                .currentTab
                                                        ]?.label
                                                    }
                                                    disabled={
                                                        !this.props.editEnabled
                                                    }
                                                />
                                            </div>

                                            <MultimediaBubble
                                                key={this.state.currentTab}
                                                languages={
                                                    currentProject.languages
                                                }
                                                disabled={
                                                    !this.props.editEnabled
                                                }
                                                questionError={
                                                    questionError &&
                                                    questionError[
                                                        this.state.currentTab
                                                    ]
                                                }
                                                setValueWithLanguage={(
                                                    keyName,
                                                    value,
                                                    language,
                                                    index
                                                ) =>
                                                    this.handleRichText(
                                                        this.state.currentTab,
                                                        keyName,
                                                        value,
                                                        language,
                                                        index
                                                    )
                                                }
                                                value={
                                                    this.state.question.options[
                                                        this.state.currentTab
                                                    ]?.texts
                                                }
                                                disableAddBubble
                                            />

                                            <div className="option-settings-block ml-3">
                                                {question.options.length >
                                                    1 && (
                                                    <button
                                                        className="btn btn--md btn--orange mb-4"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            this.deleteTextCard(
                                                                this.state
                                                                    .currentTab
                                                            );
                                                        }}
                                                    >
                                                        Delete Text Card
                                                    </button>
                                                )}
                                            </div>
                                        </>
                                    )}

                                {this.state.question.piping && (
                                    <Options
                                        name="concept"
                                        question={question}
                                        setQuestionAsQuestion={
                                            this.setQuestionAsQuestion
                                        }
                                        setQuestion={this.setQuestion}
                                        setQuestionWithLanguage={
                                            this.setQuestionWithLanguage
                                        }
                                        setOtherOptionWithLanguage={
                                            this.setOtherOptionWithLanguage
                                        }
                                        setMultimediaText={
                                            this.NEW_setQuestionTextTranslation
                                        }
                                        handleOptions={this.handleOptions}
                                        languages={currentProject.languages}
                                        countries={currentProject.countries}
                                        error={this.state.questionError || {}}
                                        settings={{
                                            maxOptionNum: 30,
                                            hideMultiple: true,
                                            hideOptionSettings: true,
                                            hideOther: true,
                                            hideNotApplicable: true,
                                            hideAdvanceMode: true,
                                            forceOptionType:
                                                OPTION_TYPES.TEXT_ONLY,
                                            disableDnd: true,
                                            hideAdvancedFooter: false,
                                            disableMenu: true,
                                            hideEditDesign: true,
                                            showHelper: true,
                                            forceRandomize: true
                                        }}
                                        helperText={
                                            DEFAULT_TEXT.QUESTION.TRADE_OFF
                                                .UPLOADER_SUBLABEL
                                        }
                                        showError={this.state.showError}
                                        disabled={!this.props.editEnabled}
                                        handleTradeoffPiping={
                                            this.handleTradeoffTextPiping
                                        }
                                        isTradeoffTextPiping={
                                            this.state.question.piping
                                        }
                                    />
                                )}
                            </React.Fragment>
                        )}
                        {this.state.question.tradeoffSettings.tradeoffTypes ===
                            TRADE_OFF_TYPES.IMAGE && (
                            <Options
                                name="concept"
                                question={question}
                                questionSummaries={this.props.questionSummaries}
                                setQuestionAsQuestion={
                                    this.setQuestionAsQuestion
                                }
                                setQuestion={this.setQuestion}
                                setQuestionWithLanguage={
                                    this.setQuestionWithLanguage
                                }
                                setOtherOptionWithLanguage={
                                    this.setOtherOptionWithLanguage
                                }
                                setMultimediaText={
                                    this.NEW_setQuestionTextTranslation
                                }
                                handleOptions={this.handleOptions}
                                languages={currentProject.languages}
                                countries={currentProject.countries}
                                error={this.state.questionError || {}}
                                settings={{
                                    maxOptionNum: 30,
                                    hideMultiple: true,
                                    hideOptionSettings: true,
                                    hideOther: true,
                                    hideNotApplicable: true,
                                    hideAdvanceMode: true,
                                    forceOptionType: OPTION_TYPES.IMAGE_ONLY,
                                    disableDnd: true,
                                    hideAdvancedFooter: false,
                                    disableMenu: true,
                                    hideEditDesign: true,
                                    showHelper: true,
                                    forceRandomize: true
                                }}
                                helperText={
                                    DEFAULT_TEXT.QUESTION.TRADE_OFF
                                        .UPLOADER_SUBLABEL
                                }
                                showError={this.state.showError}
                                disabled={!this.props.editEnabled}
                                isLive={
                                    currentProject.status == PROJECT_STATUS.LIVE
                                }
                                previousPackageQuestion={
                                    (currentProject.previousPackage &&
                                        currentProject.previousPackage.questions.find(
                                            (q) => q.id == question.id
                                        )) ||
                                    null
                                }
                            />
                        )}
                        <div className="option-settings-block">
                            <div className="row">
                                <label className="col-md-12 form-group-header">
                                    Option types
                                </label>
                                <div className="col-md-12 form-group">
                                    <InputRadio
                                        options={[
                                            {
                                                label: '2 options (Like, Dislike)',
                                                value: 2
                                            },
                                            {
                                                label: '3 options (Like, Not sure, Dislike)',
                                                value: 3
                                            },
                                            {
                                                label: '5 options (Like, Not sure, Dislike)',
                                                value: 5
                                            }
                                        ]}
                                        name="buttonType"
                                        selectedValue={this.state.buttonType}
                                        setValue={(value) =>
                                            this.onButtonChanged(value)
                                        }
                                        disabled={
                                            !this.props.editEnabled ||
                                            this.state.question.isVisible
                                        }
                                    />
                                </div>
                            </div>
                            {!this.state.question.isVisible && (
                                <div className="form-group-header display-flex">
                                    Options
                                    <CollaspeButton
                                        isOptionExpand={
                                            this.state.isOptionExpand
                                        }
                                        toggleOptionExpand={
                                            this.toggleOptionExpand
                                        }
                                    />
                                </div>
                            )}

                            {/* TODO: Change this markup */}
                            {(questionError &&
                                (questionError.buttons || {}).isError && (
                                    <div
                                        className={`form-group ${
                                            questionError ? 'error' : ''
                                        }`}
                                    >
                                        <label className="form-group__label form-group__label--block">
                                            {alertMessage.BUTTON_ERROR}
                                        </label>
                                    </div>
                                )) ||
                                null}
                            {/** Show Option Cards according to option type */}
                            {isOptionExpand &&
                                this.renderOptionCard(
                                    OPTION_TYPES.IMAGE_AND_TEXT,
                                    {
                                        disableValue: true,
                                        disableImage: true,
                                        disableRemove: true,
                                        disableDrag: true
                                    },
                                    'buttons'
                                )}
                        </div>
                        <div className="form-group-header">
                            Follow-up question
                            <Toggle
                                onChange={(value) =>
                                    this.setQuestion('showFollowUp', value)
                                }
                                checked={question.showFollowUp || false}
                                disabled={!this.props.editEnabled}
                                className="ml-auto"
                            />
                        </div>
                        {question.showFollowUp
                            ? followUpType.map((f, i) => {
                                  return (
                                      <>
                                          <MultimediaBubble
                                              key={`followUpTexts-${f.keyName}_${i}`}
                                              languages={
                                                  currentProject.languages
                                              }
                                              keyName={f.keyName}
                                              question={question}
                                              setValue={this.setQuestion}
                                              setValueWithLanguage={
                                                  this
                                                      .NEW_setQuestionTextTranslation
                                              }
                                              disableAddBubble
                                              defaultText={f.text}
                                              questionError={questionError}
                                              disabled={!this.props.editEnabled}
                                              questionText={f.label}
                                          />
                                      </>
                                  );
                              })
                            : null}
                    </div>
                    {/*end of builder body*/}
                </div>
            </div>
        );
    }
}

export default NotifyHOC(
    compose(
        withTranslation(),
        connect(
            mapStateToQuestionFormBaseProps,
            mapDispatchToQuestionFormBaseProps
        ),
        withRouter
    )(TradeOff)
);
